<script setup lang="ts">
import { CashIcon, IdentificationIcon, OfficeBuildingIcon, StarIcon } from '@heroicons/vue/solid'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { useLegalAccountDataState } from '../../../../utilities/account/useAccountDataLegal'
import AccountKycProcessTabs, { KycDocumentTabs } from '../../common/verification/AccountKycProcessTabs.vue'
import { i18n } from '../../../../localization'
import BaseTable, { DataTableHeader } from '../../../base/table/BaseTable.vue'
import { isVisibleLegalProfileDocuments } from '../../../../validation'
import {
    LegalAccountNavigationStore,
    useLegalAccountNavigationState,
} from '../../../../utilities/account/useAccountNavigationLegal'
import { FrontendBankAccountResource, putBankAccountUpdate } from '../../../../utilities/api'
import { notifyError, notifySuccess } from '../../../../utilities/useNotifications'
import EmptyState from '../../../base/EmptyState.vue'
import BankAccountStatusIndicator from '../../common/statusIndicator/BankAccountStatusIndicator.vue'
import { BankAccountStatus } from '../../../../typings'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import AccountDepotForm from '../../common/verification/AccountDepotForm.vue'

const navigationStore = useLegalAccountNavigationState() as LegalAccountNavigationStore
const store = useLegalAccountDataState()
const { legalProfile, kycDocuments: accountKycDocuments } = store
const platformSettings = getPlatformSettings()
const showDepot = computed(() => platformSettings.investment_desk_settings.show_depot_fields_on_profile_section)

onBeforeMount(() => {
    setKycDocs()
})

watch(accountKycDocuments, () => {
    setKycDocs()
})

function setKycDocs() {
    Object.keys(kycDocuments).forEach((key) => {
        if (kycDocuments.hasOwnProperty(key)) {
            kycDocuments[key].documents = []
        }
    })
    accountKycDocuments.forEach((kycD) => {
        if (kycDocuments.hasOwnProperty(kycD.verification_type)) {
            kycDocuments[kycD.verification_type].documents.push(kycD)
        }
    })
}

const kycDocuments = reactive({
    kyc_legal: {
        header: i18n.global.t('verification.legal_verification'),
        icon: IdentificationIcon,
        accepted: computed(() => legalProfile.kyc_legal),
        required: getPlatformSettings().require_kyc_id_for_investments,
        documents: [],
        show: computed(() => isVisibleLegalProfileDocuments('legal')),
        subheadline: i18n.global.t('verification.legal_verification_subheadline'),
    },
    kyc_bank: {
        header: i18n.global.t('verification.legal_bank_verification'),
        accepted: computed(() => legalProfile.kyc_bank),
        required: getPlatformSettings().require_kyc_bank_for_investments,
        icon: CashIcon,
        show: computed(() => isVisibleLegalProfileDocuments('bank')),
        subheadline: i18n.global.t('verification.legal_bank_verification_subheadline'),
        documents: [],
    },
    depot: {
        header: i18n.global.t('verification.depot'),
        icon: OfficeBuildingIcon,
        show: showDepot,
        subheadline: i18n.global.t('verification.depot_verification_subheadline'),
    },
})

// if no tab is provided select kyc_legal
const currentTabKey = ref<keyof KycDocumentTabs>(navigationStore.tab || 'kyc_legal')
watch(currentTabKey, (val) => (navigationStore.tab = val))

const bankAccountHeaders: DataTableHeader[] = [
    {
        text: i18n.global.t('bank_account.holder'),
        value: 'holder',
    },
    {
        text: i18n.global.t('bank_account.iban'),
        value: 'iban',
    },
    {
        text: i18n.global.t('bank_account.status'),
        value: 'status',
    },
    {
        text: i18n.global.t('bank_account.is_main'),
        value: 'is_main',
        classes: 'text-right',
    },
]

function setAsMainBankAccount(bankAccount: FrontendBankAccountResource) {
    putBankAccountUpdate(bankAccount.id, { is_main: true })
        .then(() => {
            notifySuccess(i18n.global.t('bank_account.set_as_main_bank_account_success_message'))
            store.fetchKycDocuments()
            store.fetchBankAccounts()
        })
        .catch((error) => {
            notifyError(error.response?.data?.errorMessage)
        })
}
</script>

<template>
    <AccountKycProcessTabs v-model:tab-key="currentTabKey" :kyc-document-tabs="kycDocuments" :account-store="store">
        <template #additional-header-content>
            <div v-if="currentTabKey === 'kyc_bank'">
                <h3 class="mb-2 mt-6 text-lg font-medium leading-6 text-gray-900">
                    {{ i18n.global.t('bank_account.bank_accounts') }}
                </h3>
                <div class="overflow-hidden rounded-md border border-gray-200">
                    <BaseTable
                        v-if="store.bankAccounts.length > 0"
                        :items="store.bankAccounts"
                        :headers="bankAccountHeaders">
                        <template #item.status="{ value }">
                            <BankAccountStatusIndicator :status="value" />
                        </template>
                        <template #item.is_main="{ item }">
                            <div>
                                <small v-if="item.is_main" class="flex justify-end text-sm text-blue-800">
                                    {{ $t('bank_account.main_bank_account') }}
                                    <StarIcon class="ml-1 w-4" />
                                </small>
                                <a
                                    v-else-if="item.status === BankAccountStatus.VERIFIED"
                                    class="cursor-pointer text-right text-sm text-gray-900"
                                    @click="setAsMainBankAccount(item)">
                                    {{ $t('bank_account.set_as_main_bank_account') }}
                                </a>
                            </div>
                        </template>
                    </BaseTable>
                    <EmptyState
                        v-else
                        classes="my-4"
                        :header="$t('bank_account.empty_state_header')"
                        :description="$t('bank_account.empty_state_description')" />
                </div>
            </div>
        </template>
        <template #body>
            <div v-if="currentTabKey === 'depot'">
                <AccountDepotForm profile-type="legal" />
            </div>
        </template>
    </AccountKycProcessTabs>
</template>
